import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/Button";
import { Loader } from "../components/Loader";
import { formatInteger } from "../helpers/formatNumber";
import {
  downloadOptInCustomersCsv,
  getOptInCustomers,
  getOptInPartners,
  OptInCustomer,
  OptInCustomersPage,
  OptInPartner,
  OptInStatusFilter,
} from "../api/optInOffers";
import { OptInPartnerCards } from "../components/AdminOptInOffers/OptInPartnerCards";
import { OptInPersonModal } from "../components/AdminOptInOffers/OptInPersonModal";
import { OptInCustomersTable } from "../components/AdminOptInOffers/OptInCustomersTable";
import { OptInTransactionsTab } from "../components/AdminOptInOffers/OptInTransactionsTab";
import { copenhagenToday, slug } from "../components/AdminOptInOffers/exportFileName";

const PAGE_SIZE = 50;

const STATUS_FILTERS: OptInStatusFilter[] = ["All", "OptedIn", "Missed", "NotCompensated"];

const TABS = [
  { key: "customers", label: "Customers" },
  { key: "transactions", label: "Transactions" },
] as const;

const parseStatus = (value: string | null): OptInStatusFilter =>
  STATUS_FILTERS.includes(value as OptInStatusFilter)
    ? (value as OptInStatusFilter)
    : "All";

const AdminOptInOffers: React.FC = () => {
  const [partners, setPartners] = useState<OptInPartner[]>([]);
  const [customers, setCustomers] = useState<OptInCustomer[]>([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState<OptInCustomersPage["counts"]>({
    all: 0,
    optedIn: 0,
    missed: 0,
    notCompensated: 0,
  });
  const [initialLoad, setInitialLoad] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const [searchParams, setSearchParams] = useSearchParams();
  // URL is the single source of truth for the partner, status, applied search and page.
  const selectedBusinessId = searchParams.get("business") ?? "";
  const status = parseStatus(searchParams.get("status"));
  const appliedSearch = searchParams.get("search") ?? "";
  const pageRaw = parseInt(searchParams.get("page") ?? "1", 10);
  const pageNumber = Number.isFinite(pageRaw) && pageRaw >= 1 ? pageRaw : 1;

  const personId = searchParams.get("person");

  const activeTab = TABS.find((t) => t.key === searchParams.get("tab")) ?? TABS[0];

  const [search, setSearch] = useState(appliedSearch);

  useEffect(() => {
    setSearch(searchParams.get("search") ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const buildParams = (
    businessId: string,
    statusValue: OptInStatusFilter,
    searchValue: string,
    page: number,
    tab: string = activeTab.key
  ): URLSearchParams => {
    const params = new URLSearchParams();
    if (tab !== "customers") params.set("tab", tab);
    if (businessId) params.set("business", businessId);
    if (statusValue !== "All") params.set("status", statusValue);
    const trimmed = searchValue.trim();
    if (trimmed) params.set("search", trimmed);
    if (page > 1) params.set("page", String(page));
    return params;
  };

  useEffect(() => {
    let ignore = false;
    const run = async () => {
      try {
        const partnerList = await getOptInPartners();
        if (ignore) return;
        setPartners(partnerList);
        // No partner selected yet, or the URL names one that is not (or no longer) a partner —
        // pick the first one so the page is useful immediately.
        const known = partnerList.some(
          (p) => p.businessContentfulId === selectedBusinessId
        );
        if (!known && partnerList.length > 0) {
          setSearchParams(
            buildParams(partnerList[0].businessContentfulId, "All", "", 1, "customers"),
            { replace: true }
          );
        }
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch opt-in partners");
        console.error("Error fetching opt-in partners:", err);
      } finally {
        if (!ignore) setInitialLoad(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedBusinessId || activeTab.key !== "customers") return;
    let ignore = false;
    const run = async () => {
      try {
        setLoading(true);
        const response = await getOptInCustomers(selectedBusinessId, {
          status,
          search: appliedSearch || undefined,
          pageNumber,
          pageSize: PAGE_SIZE,
        });
        if (ignore) return;
        setCustomers(response.data);
        setTotal(response.total);
        setCounts(response.counts);
        setError(null);
      } catch (err) {
        if (ignore) return;
        setError("Failed to fetch opt-in customers");
        console.error("Error fetching opt-in customers:", err);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessId, status, appliedSearch, pageNumber, activeTab.key]);

  if (initialLoad) {
    return (
      <div className="loading-page">
        <Loader />
      </div>
    );
  }

  const selectedPartner = partners.find(
    (p) => p.businessContentfulId === selectedBusinessId
  );
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const chips: { value: OptInStatusFilter; label: string; count: number }[] = [
    { value: "All", label: "All", count: counts.all },
    { value: "OptedIn", label: "Opted in", count: counts.optedIn },
    { value: "Missed", label: "Missed", count: counts.missed },
    { value: "NotCompensated", label: "Not compensated", count: counts.notCompensated },
  ];

  const handleExport = async () => {
    if (!selectedPartner) return;
    setExporting(true);
    setExportError(null);
    const fileName = `opt-ins-${slug(
      selectedPartner.businessName ?? selectedPartner.businessContentfulId
    )}-${copenhagenToday()}.csv`;
    try {
      await downloadOptInCustomersCsv(
        selectedBusinessId,
        { status, search: appliedSearch || undefined },
        fileName
      );
    } catch (err) {
      // Blob responses hide the backend's JSON error body, so show a generic message.
      setExportError("Export failed. Please try again.");
      console.error("Error exporting opt-in customers:", err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col gap-4 mb-10">
        <h2 className="font-medium">Opt-in Offers</h2>

        {partners.length === 0 ? (
          <div className="text-gray-500">No opt-in partners yet.</div>
        ) : (
          <OptInPartnerCards
            partners={partners}
            selectedBusinessId={selectedBusinessId}
            onSelect={(businessId) =>
              setSearchParams(buildParams(businessId, "All", "", 1))
            }
          />
        )}

        {selectedPartner && (
          <div className="flex flex-wrap gap-1 border-b border-gray-200">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                type="button"
                onClick={() =>
                  setSearchParams(buildParams(selectedBusinessId, "All", "", 1, tab.key))
                }
                className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                  tab.key === activeTab.key
                    ? "border-primary text-primary"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        )}

        {selectedPartner && activeTab.key === "customers" && (
          <>
            <div className="flex flex-wrap gap-2">
              {chips.map((chip) => (
                <button
                  key={chip.value}
                  type="button"
                  onClick={() =>
                    setSearchParams(
                      buildParams(selectedBusinessId, chip.value, appliedSearch, 1)
                    )
                  }
                  className={`px-3 py-1 rounded-full text-sm border ${
                    status === chip.value
                      ? "bg-purple-500 text-white border-purple-500"
                      : "bg-white text-gray-700 border-gray-300"
                  }`}
                >
                  {chip.label} ({formatInteger(chip.count)})
                </button>
              ))}
            </div>

            <form
              className="flex flex-wrap items-end gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                setSearchParams(buildParams(selectedBusinessId, status, search, 1));
              }}
            >
              <div className="w-[360px]">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Search (name, email)
                </label>
                <input
                  type="text"
                  className="w-full p-2 border border-gray-300 rounded-md"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="e.g. a name or an email"
                />
              </div>
              <div className="flex items-center gap-3">
                <Button type="submit">Apply</Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setSearch("");
                    setSearchParams(buildParams(selectedBusinessId, status, "", 1));
                  }}
                >
                  Reset
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleExport}
                  disabled={exporting}
                >
                  Export CSV
                </Button>
              </div>
            </form>
            {exportError && <div className="error">{exportError}</div>}
          </>
        )}
      </div>

      {selectedPartner && activeTab.key === "transactions" && (
        <OptInTransactionsTab
          businessId={selectedBusinessId}
          partnerName={selectedPartner.businessName ?? selectedPartner.businessContentfulId}
        />
      )}

      {loading && activeTab.key === "customers" && (
        <div className="flex justify-center my-8">
          <Loader />
        </div>
      )}

      {error && (activeTab.key === "customers" || partners.length === 0) && (
        <div className="error mb-4">{error}</div>
      )}

      {selectedPartner && activeTab.key === "customers" && (
        <>
          <OptInCustomersTable
            customers={customers}
            onSelectPerson={(identityId) => {
              const params = buildParams(selectedBusinessId, status, appliedSearch, pageNumber);
              params.set("person", identityId);
              setSearchParams(params);
            }}
            loading={loading}
            emptyMessage={
              appliedSearch || status !== "All"
                ? "No customers match this filter."
                : "No customers yet."
            }
          />
          <div className="flex justify-between items-center mt-4">
            <Button
              onClick={() =>
                setSearchParams(
                  buildParams(
                    selectedBusinessId,
                    status,
                    appliedSearch,
                    Math.max(1, pageNumber - 1)
                  )
                )
              }
              disabled={pageNumber === 1}
              variant="outline"
            >
              Previous
            </Button>
            <span>
              Page {pageNumber} of {totalPages} · {formatInteger(total)} customers
            </span>
            <Button
              onClick={() =>
                setSearchParams(
                  buildParams(selectedBusinessId, status, appliedSearch, pageNumber + 1)
                )
              }
              disabled={pageNumber >= totalPages}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </>
      )}

      {selectedPartner && personId && (
        <OptInPersonModal
          key={`${selectedBusinessId}:${personId}`}
          businessId={selectedBusinessId}
          partnerName={selectedPartner.businessName ?? selectedPartner.businessContentfulId}
          identityId={personId}
          onClose={() => {
            const params = new URLSearchParams(searchParams);
            params.delete("person");
            setSearchParams(params);
          }}
        />
      )}
    </div>
  );
};

export default AdminOptInOffers;
